import { RefObject } from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

import type {
  ScriptStep,
  UserInputMode,
} from '@shared/interfaces/assistantScripts';
import type { PollyLanguage } from '@shared/interfaces/polly';
import type { AssistantModalTheme } from '@shared/components/AssistantEditorModals/theme';
import type { AudioPlaybackKey } from '../hooks/useAudioPreview';
import {
  StepsHeaderRow,
  StepsTitle,
  AddStepButton,
  FixedStepsBar,
  StepsContainer,
} from '../AssistantScriptDialog.styled';
import ScriptStepItem from '../ScriptStepItem';

interface DialogueStepsListProps {
  scriptSteps: ScriptStep[];
  stepExpanded: boolean[];
  stepUserInputModes: UserInputMode[];
  activeLanguage: PollyLanguage;
  effectiveBaseLanguage: PollyLanguage;
  audioPlaybackKey: AudioPlaybackKey | null;
  isAudioPlaying: boolean;
  userFirstName?: string;
  mapName?: string;
  isAddStepFixed: boolean;
  stepsHeaderRef: RefObject<HTMLDivElement>;
  scriptStepsEndRef: RefObject<HTMLDivElement>;
  modalTheme: AssistantModalTheme;
  onToggleExpanded: (index: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onUpdateStep: (index: number, data: Partial<ScriptStep>) => void;
  onUpdateStepTranslation: (
    index: number,
    lang: PollyLanguage,
    value: string,
  ) => void;
  onUserInputModeChange: (index: number, mode: UserInputMode) => void;
  onRemove: (index: number) => void;
  onPlay: (text: string, index: number) => void;
  onStop: () => void;
  onAddStep: () => void;
}

const DialogueStepsList = ({
  scriptSteps,
  stepExpanded,
  stepUserInputModes,
  activeLanguage,
  effectiveBaseLanguage,
  audioPlaybackKey,
  isAudioPlaying,
  userFirstName,
  mapName,
  isAddStepFixed,
  stepsHeaderRef,
  scriptStepsEndRef,
  modalTheme,
  onToggleExpanded,
  onMoveUp,
  onMoveDown,
  onUpdateStep,
  onUpdateStepTranslation,
  onUserInputModeChange,
  onRemove,
  onPlay,
  onStop,
  onAddStep,
}: DialogueStepsListProps) => {
  const { t } = useTranslation('common');

  return (
    <Box>
      <StepsHeaderRow
        ref={stepsHeaderRef}
        sx={{ visibility: isAddStepFixed ? 'hidden' : 'visible' }}
      >
        <Box>
          <StepsTitle>{t('assistant.scriptDialog.scriptSteps')}</StepsTitle>
          {scriptSteps.length > 1 && (
            <Typography
              variant="caption"
              sx={{ color: 'var(--am-text-secondary)' }}
            >
              {t('assistant.scriptDialog.stepsOrderHint')}
            </Typography>
          )}
        </Box>
        <AddStepButton
          variant="outlined"
          size="small"
          onClick={onAddStep}
          data-testid="assistant-script-add-step-button"
        >
          <AddIcon sx={{ fontSize: 15, mr: 0.5 }} />{' '}
          {t('assistant.scriptDialog.addStep')}
        </AddStepButton>
      </StepsHeaderRow>

      {isAddStepFixed && (
        <FixedStepsBar $theme={modalTheme}>
          <Box>
            <StepsTitle>{t('assistant.scriptDialog.scriptSteps')}</StepsTitle>
            {scriptSteps.length > 1 && (
              <Typography
                variant="caption"
                sx={{ color: 'var(--am-text-secondary)' }}
              >
                {t('assistant.scriptDialog.stepsOrderHintShort')}
              </Typography>
            )}
          </Box>
          <AddStepButton
            variant="outlined"
            size="small"
            onClick={onAddStep}
            data-testid="assistant-script-add-step-button-fixed"
          >
            <AddIcon sx={{ fontSize: 15, mr: 0.5 }} />{' '}
            {t('assistant.scriptDialog.addStep')}
          </AddStepButton>
        </FixedStepsBar>
      )}

      <StepsContainer>
        {scriptSteps.map((step, index) => (
          <ScriptStepItem
            key={index}
            step={step}
            index={index}
            isFirst={index === 0}
            isLast={index === scriptSteps.length - 1}
            expanded={stepExpanded[index] ?? true}
            activeLanguage={activeLanguage}
            effectiveBaseLanguage={effectiveBaseLanguage}
            stepUserInputMode={stepUserInputModes[index] ?? 'none'}
            audioPlaybackKey={audioPlaybackKey}
            isAudioPlaying={isAudioPlaying}
            userFirstName={userFirstName}
            mapName={mapName}
            onToggleExpanded={() => onToggleExpanded(index)}
            onMoveUp={() => onMoveUp(index)}
            onMoveDown={() => onMoveDown(index)}
            onUpdateStep={(data) => onUpdateStep(index, data)}
            onUpdateStepTranslation={(lang, value) =>
              onUpdateStepTranslation(index, lang, value)
            }
            onUserInputModeChange={(mode) => onUserInputModeChange(index, mode)}
            onRemove={() => onRemove(index)}
            onPlay={onPlay}
            onStop={onStop}
          />
        ))}
      </StepsContainer>
      <div ref={scriptStepsEndRef} />
    </Box>
  );
};

export default DialogueStepsList;
